// Общие типы и справочники Tripboard — используются и сервером, и клиентом.

export const PLACE_CATEGORIES = [
  "city",
  "sight",
  "nature",
  "beach",
  "food",
  "cafe",
  "nightlife",
  "hotel",
  "activity",
  "shopping",
  "transport",
  "other",
] as const;
export type PlaceCategory = (typeof PLACE_CATEGORIES)[number];

/** Статус интереса к месту. «В плане» вычисляется по пунктам плана, «забронировано» — свойство пункта плана, не места. */
export const PLACE_STATUSES = ["idea", "want", "visited", "skipped"] as const;
export type PlaceStatus = (typeof PLACE_STATUSES)[number];

export const ITEM_KINDS = ["visit", "stay", "transport", "activity", "note"] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];

export const TRANSPORT_MODES = [
  "flight",
  "train",
  "sleeper_bus",
  "bus",
  "car",
  "motorbike",
  "boat",
  "taxi",
  "walk",
  "other",
] as const;
export type TransportMode = (typeof TRANSPORT_MODES)[number];

export const ITEM_STATUSES = ["idea", "planned", "booked", "done", "cancelled"] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];

export const MEMBER_ROLES = ["owner", "editor", "viewer"] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export const CHECKLIST_CATEGORIES = ["documents", "booking", "money", "health", "packing", "tech", "other"] as const;
export type ChecklistCategory = (typeof CHECKLIST_CATEGORIES)[number];

export const EXPENSE_CATEGORIES = ["transport", "stay", "food", "activities", "shopping", "fees", "other"] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export const SOURCE_KINDS = ["youtube", "article", "text"] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];

export const SOURCE_STATUSES = ["pending", "fetching", "extracting", "ready", "needs_ai", "error"] as const;
export type SourceStatus = (typeof SOURCE_STATUSES)[number];

export const WIDGET_TARGETS = ["trip", "place", "day"] as const;
export type WidgetTarget = (typeof WIDGET_TARGETS)[number];

export type ActorKind = "user" | "ai" | "system";

export interface UserDTO {
  id: string;
  email: string | null;
  name: string | null;
  avatarUrl: string | null;
}

export interface MemberDTO extends UserDTO {
  role: MemberRole;
  color: string;
}

export interface TripSettings {
  /** ISO 3166-1 alpha-2 коды стран — для геокодинга */
  countryCodes?: string[];
  /** центр карты по умолчанию */
  mapCenter?: [number, number];
  mapZoom?: number;
  /** курс VND за 1 EUR, введённый вручную (fallback) */
  vndPerEur?: number;
}

export interface TripDTO {
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  originName: string | null;
  originLat: number | null;
  originLng: number | null;
  homeTz: string;
  destTz: string;
  currency: string;
  coverUrl: string | null;
  settings: TripSettings;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  myRole?: MemberRole;
}

export interface PlaceDTO {
  id: string;
  tripId: string;
  name: string;
  nameLocal: string | null;
  lat: number;
  lng: number;
  category: PlaceCategory;
  status: PlaceStatus;
  region: string | null;
  address: string | null;
  description: string | null;
  notes: string | null;
  url: string | null;
  imageUrl: string | null;
  phone: string | null;
  tags: string[];
  costEstimate: number | null;
  costCurrency: string | null;
  durationMin: number | null;
  priority: number; // 0..3
  createdBy: string | null;
  createdVia: ActorKind | "source";
  sourceId: string | null;
  createdAt: string;
  updatedAt: string;
  votes?: { userId: string; value: number }[];
  commentCount?: number;
  /** последний комментарий — показываем на карте */
  lastComment?: { userId: string; body: string; createdAt: string } | null;
}

export interface ItineraryItemDTO {
  id: string;
  tripId: string;
  day: string; // YYYY-MM-DD
  position: number;
  kind: ItemKind;
  status: ItemStatus;
  placeId: string | null;
  title: string;
  startTime: string | null; // HH:MM
  endTime: string | null;
  durationMin: number | null;
  nights: number | null; // для stay
  notes: string | null;
  cost: number | null;
  currency: string | null;
  bookingRef: string | null;
  bookingUrl: string | null;
  contact: string | null;
  /** кто участвует; пусто = вся группа */
  participants: string[];
  // для переездов
  mode: TransportMode | null;
  fromName: string | null;
  fromLat: number | null;
  fromLng: number | null;
  toName: string | null;
  toLat: number | null;
  toLng: number | null;
  departAt: string | null; // ISO с часовым поясом
  arriveAt: string | null; // ISO с часовым поясом
  fromTz: string | null; // IANA, напр. Europe/Vilnius
  toTz: string | null;
  carrier: string | null;
  createdBy: string | null;
  createdVia: ActorKind;
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistItemDTO {
  id: string;
  tripId: string;
  title: string;
  category: ChecklistCategory;
  done: boolean;
  assigneeId: string | null;
  dueDate: string | null;
  notes: string | null;
  position: number;
  createdVia: ActorKind;
  createdAt: string;
}

export interface ExpenseDTO {
  id: string;
  tripId: string;
  title: string;
  amount: number;
  currency: string;
  amountEur: number | null;
  paidBy: string | null;
  splitAmong: string[]; // userIds; пусто = все участники
  category: ExpenseCategory;
  date: string | null;
  placeId: string | null;
  notes: string | null;
  createdAt: string;
}

export interface ExtractedPlace {
  name: string;
  nameLocal?: string | null;
  searchQuery: string;
  category: PlaceCategory;
  region?: string | null;
  description: string;
  timestampSec?: number | null;
  lat?: number | null;
  lng?: number | null;
  geocoded?: boolean;
  confidence?: "high" | "medium" | "low";
  addedPlaceId?: string | null;
}

export interface SourceDTO {
  id: string;
  tripId: string;
  kind: SourceKind;
  url: string | null;
  title: string | null;
  author: string | null;
  thumbnailUrl: string | null;
  durationSec: number | null;
  lang: string | null;
  status: SourceStatus;
  error: string | null;
  summary: string | null;
  tips: string[];
  extracted: ExtractedPlace[];
  hasTranscript: boolean;
  contentChars: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WidgetDTO {
  id: string;
  tripId: string;
  title: string;
  description: string | null;
  html: string;
  targetType: WidgetTarget;
  targetId: string | null;
  threadId: string | null;
  version: number;
  createdVia: ActorKind;
  createdAt: string;
  updatedAt: string;
}

export interface CommentDTO {
  id: string;
  targetType: "place" | "item";
  targetId: string;
  userId: string;
  body: string;
  createdAt: string;
}

export interface ActivityDTO {
  id: number;
  actorId: string | null;
  actorKind: ActorKind;
  entity: string;
  entityId: string;
  op: "create" | "update" | "delete";
  summary: string;
  batchId: string | null;
  createdAt: string;
  undoneAt: string | null;
}

/** Полный снимок поездки — одним запросом */
export interface TripBundle {
  trip: TripDTO;
  members: MemberDTO[];
  places: PlaceDTO[];
  items: ItineraryItemDTO[];
  checklist: ChecklistItemDTO[];
  expenses: ExpenseDTO[];
  sources: SourceDTO[];
  widgets: WidgetDTO[];
}

/** Событие realtime (SSE) */
export type TripEvent =
  | { type: "change"; entity: EntityName; op: "upsert" | "delete"; id: string; data?: unknown; actorId?: string | null }
  | { type: "reload"; reason?: string }
  | { type: "presence"; userIds: string[] }
  /** «покажи на карте» из внешнего ИИ (MCP) — двигает карту у этого пользователя */
  | { type: "focus"; userId: string; bounds?: [[number, number], [number, number]]; center?: [number, number]; zoom?: number; day?: string | null }
  | { type: "ping" };

export type EntityName =
  | "trip"
  | "member"
  | "place"
  | "item"
  | "checklist"
  | "expense"
  | "source"
  | "widget"
  | "comment"
  | "vote";

// ---------- ИИ-чат ----------

/** События SSE-стрима ответа ИИ, которые получает клиент */
export type ChatStreamEvent =
  | { type: "start"; threadId: string; messageId: string }
  | { type: "text"; delta: string }
  | { type: "thinking"; delta: string }
  | { type: "tool_start"; id: string; name: string; label: string }
  | { type: "tool_end"; id: string; name: string; ok: boolean; summary: string }
  | { type: "server_tool"; name: string; label: string }
  | { type: "map_focus"; bounds?: [[number, number], [number, number]]; center?: [number, number]; zoom?: number; day?: string | null; placeIds?: string[] }
  | { type: "widget"; widgetId: string; title: string }
  | { type: "done"; batchId: string | null; changes: number; usage?: { input: number; output: number; cacheRead: number } }
  | { type: "error"; message: string };

export interface ChatToolCallView {
  id: string;
  name: string;
  label: string;
  ok?: boolean;
  summary?: string;
}

/** Как сообщение чата показывается в UI (без сырого формата API) */
export interface ChatMessageView {
  id: string;
  role: "user" | "assistant";
  text: string;
  thinking?: string;
  tools?: ChatToolCallView[];
  widgetIds?: string[];
  batchId?: string | null;
  changes?: number;
  undone?: boolean;
  fromWidget?: { widgetId: string; title: string } | null;
  authorId?: string | null;
  createdAt: string;
  error?: string | null;
}

export interface ChatThreadDTO {
  id: string;
  tripId: string;
  userId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

// ---------- утилиты дат (без часовых поясов: день = локальная дата поездки) ----------

export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

export function daysBetween(a: string, b: string): number {
  const [y1, m1, d1] = a.split("-").map(Number);
  const [y2, m2, d2] = b.split("-").map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
}

export function tripDays(start: string, end: string): string[] {
  const n = daysBetween(start, end);
  const out: string[] = [];
  for (let i = 0; i <= n; i++) out.push(addDays(start, i));
  return out;
}

/** Дни, которые покрывает ночёвка: заезд day, N ночей → дни day..day+N-1 (ночи) */
export function stayNights(day: string, nights: number | null): string[] {
  const n = Math.max(1, nights ?? 1);
  return Array.from({ length: n }, (_, i) => addDays(day, i));
}

// ---------- умное добавление ----------

export interface QuickAddPlace {
  existingPlaceId: string | null;
  name: string;
  nameLocal: string | null;
  category: PlaceCategory;
  region: string | null;
  address: string | null;
  description: string | null;
  url: string | null;
  phone: string | null;
  lat: number | null;
  lng: number | null;
  coordsFrom: "existing" | "link" | "page" | "geocoder" | "ai" | null;
}

export interface QuickAddItem {
  kind: ItemKind;
  title: string;
  day: string | null;
  startTime: string | null;
  endTime: string | null;
  durationMin: number | null;
  nights: number | null;
  status: ItemStatus | null;
  mode: TransportMode | null;
  fromName: string | null;
  fromLat: number | null;
  fromLng: number | null;
  toName: string | null;
  toLat: number | null;
  toLng: number | null;
  departAt: string | null;
  arriveAt: string | null;
  fromTz: string | null;
  toTz: string | null;
  carrier: string | null;
  bookingRef: string | null;
  bookingUrl: string | null;
  contact: string | null;
  cost: number | null;
  currency: string | null;
  notes: string | null;
}

export interface QuickAddResult {
  intent: "place" | "item";
  place: QuickAddPlace | null;
  item: QuickAddItem | null;
  warnings: string[];
}
